import { get } from 'lodash'
import { useParams } from 'react-router-dom'

import { Colour, IconName } from '@acre/design-system'
import { useGetCurrentUserQuery, useGetUserQuery, useIsProtectionAuthValidLazyQuery, useIsProtectionAuthValidV2LazyQuery } from '@acre/graphql'
import type { EncryptFn } from '@acre/utils'
import {
  encryptApplicationData,
  encryptProtectionData,
  getMetadata,
  getMetadataIndex,
  useFlag,
} from '@acre/utils'

/**
 * Resolves the user ID from route params (falling back to the current logged-in user
 * when there is no :id route param, e.g. during onboarding) and fetches the user.
 */
export const useResolvedUser = () => {
  const { id: paramId } = useParams<{ id?: string }>()
  const { data: currentUserData } = useGetCurrentUserQuery({ skip: !!paramId })
  const userId = paramId ?? currentUserData?.currentUser?.id ?? ''

  const { data, loading, error } = useGetUserQuery({
    variables: { id: userId!, useClientApi: false },
    skip: !userId,
  })

  return { userId, data, loading, error }
}

//
// Types
//

export type CredentialsPair = {
  username?: Maybe<string>
  password?: Maybe<string>
}

export type LVCredentials = {
  agent?: string
}

export type IressCredentialsPair = {
  username?: Maybe<string>
  password?: Maybe<string>
  user_code?: Maybe<string>
}

export type UserCredentialsSchema = {
  uinsure?: CredentialsPair
  ipipeline?: CredentialsPair
  paymentshield?: CredentialsPair
  lv?: LVCredentials
  lloyds?: CredentialsPair
  iress?: IressCredentialsPair
}

export type MetadataMapping = {
  formKey: string
  metadataKey: string
  encryptionMethod?: EncryptFn
}

export const getLVFieldsFromMetadata = (metadata: UserMetadata[]) => {
  return {
    agent: getMetadata(metadata, 'lv_agent_id'),
  }
}

export const getCredsFromMetadata = (
  metadata: UserMetadata[],
  provider: GeneralInsuranceProvider | ProtectionProvider | string,
  plainText: boolean,
): CredentialsPair => {
  switch (provider) {
    case 'PAYMENTSHIELD':
      return {
        username: getMetadata(metadata, `paymentshield_seller_id${plainText ? '_plaintext' : ''}`),
        password: getMetadata(metadata, 'paymentshield_passkey'),
      }
    case 'UINSURE':
      return {
        username: getMetadata(metadata, `uinsure_username${plainText ? '_plaintext' : ''}`),
        password: getMetadata(metadata, 'uinsure_password'),
      }
    case 'IPIPELINE':
      return {
        username: getMetadata(metadata, `protection.IPUsername${plainText ? '_plaintext' : ''}`),
        password: getMetadata(metadata, 'protection.IPPassword'),
      }
    default:
      return {
        username: null,
        password: null,
      }
  }
}

// only check if cred exists doesn't check if it is valid
export const areCredentialsProvided = (cred: CredentialsPair | LVCredentials) => {
  if ('username' in cred && 'password' in cred) {
    return Boolean(cred.username && cred.password)
  }

  if ('agent' in cred) {
    return Boolean(cred.agent)
  }

  return false
}

export const useLVFieldsFromMetadata = () => {
  const { data } = useResolvedUser()
  const typedMetaData: UserMetadata[] = data?.user?.metadata || []

  return getLVFieldsFromMetadata(typedMetaData)
}

export const useCredsFromMetadata = (
  provider: GeneralInsuranceProvider | ProtectionProvider | string,
  plainText: boolean = false,
) => {
  const { data } = useResolvedUser()
  const typedMetaData: UserMetadata[] = data?.user?.metadata || []

  return getCredsFromMetadata(typedMetaData, provider, plainText)
}

/**
 * Build the initial values for the form
 * @param metadata
 * @returns user credentials
 */
export const buildInitialValues = (metadata: UserMetadata[] | null, credentials: UserCredentials[]) => {
  if (!metadata) return {}
  // Santander
  const santanderBrokerCode = getMetadata(metadata, 'lender.santander.brokercode')
  // LV
  const lvAgent = getMetadata(metadata, 'lv_agent_id')

  // Lloyds
  const lloyds = credentials.find((cred) => cred.provider === 'Application_DIP_LBG')

  // Iress
  const iress = credentials.find((cred) => cred.provider === 'PROTECTION_SOURCING_IRESS')

  return {
    ipipeline: getCredsFromMetadata(metadata, 'IPIPELINE', true),
    uinsure: getCredsFromMetadata(metadata, 'UINSURE', true),
    paymentshield: getCredsFromMetadata(metadata, 'PAYMENTSHIELD', true),
    santander: {
      brokercode: santanderBrokerCode,
    },
    lloyds: {
      username: lloyds?.username,
      password: lloyds?.password,
    },
    iress: {
      username: iress?.username,
      password: iress?.password,
      user_code: iress?.metadata?.IRESS_USER_CODE,
    },
    lv: {
      agent: lvAgent,
    },
  }
}

//
// Managing metadata
//
export const mergeCurValsIntoMetadata = (
  initialValues: UserCredentialsSchema,
  currentValues: UserCredentialsSchema,
  metaData: UserMetadata[],
  metadataMappings: MetadataMapping[],
) => {
  //
  // Metadata is quite fragile, so we need to be sure that we only edit/add
  // items that are relevant. This means that:
  //
  // - if a field is pristine we don't want to re-encrypt it
  // - we need to ensure we don't overwrite/delete existing unrelated items
  //
  return metadataMappings.reduce(async (nmd, mapping) => {
    const previous = await nmd
    const initial: string = get(initialValues, mapping.formKey)
    const current: string = get(currentValues, mapping.formKey)

    // Do nothing if values match
    if (initial === current) {
      return previous
    }

    // Or if the corresponding metadata item already exists, overwrite it
    if (getMetadataIndex(metaData, mapping.metadataKey) > -1) {
      return await overwriteMetadata(previous, mapping, current)
    }

    // If metadata item doesn't exist, create it instead
    return await createMetadata(previous, mapping, current)

    // Accumulator values return promises, so initial value needs to be passed as a promise
  }, Promise.resolve(metaData))
}

// Warning: This method has side effects, because it edits an item within 'metadata'
export const overwriteMetadata = async (metadata: UserMetadata[], mapping: MetadataMapping, value: string) => {
  const newMetadata = [...metadata]
  const { metadataKey, encryptionMethod } = mapping
  const index = getMetadataIndex(metadata!, metadataKey)
  const newItem = { ...metadata![index] }

  // The value may or may not need to be encrypted
  let newValue
  if (encryptionMethod) newValue = await encryptionMethod(value || '')
  else newValue = value

  newItem.string_value = newValue
  newMetadata.splice(index, 1, newItem)
  return newMetadata
}

// Warning: This method has side effects, because it adds an item to 'metadata'
export const createMetadata = async (metadata: UserMetadata[], mapping: MetadataMapping, value: string) => {
  const newMetadata = [...metadata]
  const { metadataKey, encryptionMethod } = mapping
  const newItem = { name: metadataKey, user_editable: true, string_value: '' }

  // The value may or may not need to be encrypted
  let newValue
  if (encryptionMethod) newValue = await encryptionMethod(value || '')
  else newValue = value

  newItem.string_value = newValue
  newMetadata.push(newItem)
  return newMetadata
}

export type CredentialsStatusType = 'valid' | 'check' | 'invalid' | 'save' | 'error'

export const CREDENTIALS_BANNER_VALID: CredentialsStatusType = 'valid'
export const CREDENTIALS_BANNER_CHECK: CredentialsStatusType = 'check'
export const CREDENTIALS_BANNER_INVALID: CredentialsStatusType = 'invalid'
export const CREDENTIALS_BANNER_SAVE_PROMPT: CredentialsStatusType = 'save'
export const CREDENTIALS_BANNER_ERROR: CredentialsStatusType = 'error'

export const bannerPrefix = 'userSettings.credentials.banners.'

export const CredentialsBannerMessage: { [key in CredentialsStatusType]: string } = {
  [CREDENTIALS_BANNER_VALID]: `${bannerPrefix}credentialsValid`,
  [CREDENTIALS_BANNER_CHECK]: `${bannerPrefix}checkCreds`,
  [CREDENTIALS_BANNER_INVALID]: `${bannerPrefix}unableToValidate`,
  [CREDENTIALS_BANNER_SAVE_PROMPT]: `${bannerPrefix}newCreditsValid`,
  [CREDENTIALS_BANNER_ERROR]: `${bannerPrefix}providerUnavailable`,
}

export const CredentialsBannerColour: { [key in CredentialsStatusType]: Colour } = {
  [CREDENTIALS_BANNER_VALID]: Colour.Mint,
  [CREDENTIALS_BANNER_CHECK]: Colour.Navy,
  [CREDENTIALS_BANNER_INVALID]: Colour.Yellow,
  [CREDENTIALS_BANNER_SAVE_PROMPT]: Colour.Mint,
  [CREDENTIALS_BANNER_ERROR]: Colour.Red,
}

export const CredentialsBannerIcon: { [key in CredentialsStatusType]: IconName } = {
  [CREDENTIALS_BANNER_VALID]: IconName.CheckCircle,
  [CREDENTIALS_BANNER_CHECK]: IconName.Info,
  [CREDENTIALS_BANNER_INVALID]: IconName.AlertCircle,
  [CREDENTIALS_BANNER_SAVE_PROMPT]: IconName.Info,
  [CREDENTIALS_BANNER_ERROR]: IconName.AlertCircle,
}

export const createCredentialsPayload = async (
  initialValues: UserCredentialsSchema,
  currentValues: UserCredentialsSchema,
) => {
  const credentials = []

  // Lloyds
  if (currentValues.lloyds) {
    const currentPassword = currentValues.lloyds.password
    const initialPassword = initialValues?.lloyds?.password

    //if passwords match, do nothing, we dont want to reencrypt a password again
    if (currentPassword === initialPassword) {
      credentials.push({
        provider: 'Application_DIP_LBG',
        username: currentValues.lloyds.username,
        password: currentPassword,
      })
    } else {
      const password = currentValues.lloyds?.password
        ? await encryptApplicationData(currentValues?.lloyds?.password)
        : ''
      credentials.push({
        provider: 'Application_DIP_LBG',
        username: currentValues.lloyds.username,
        password: password,
      })
    }
  }

  // Iress
  if (currentValues.iress) {
    const currentPassword = currentValues.iress.password
    const initialPassword = initialValues?.iress?.password

    //if passwords match, do nothing, we dont want to reencrypt a password again
    if (currentPassword === initialPassword) {
      credentials.push({
        provider: 'PROTECTION_SOURCING_IRESS',
        username: currentValues.iress.username,
        password: currentPassword,
        metadata: {
          IRESS_USER_CODE: currentValues.iress.user_code,
        },
      })
    } else {
      const password = currentValues.iress?.password ? await encryptProtectionData(currentValues?.iress?.password) : ''
      credentials.push({
        provider: 'PROTECTION_SOURCING_IRESS',
        username: currentValues.iress.username,
        password: password,
        metadata: {
          IRESS_USER_CODE: currentValues.iress.user_code,
        },
      })
    }
  }

  // Add more lenders here in the future
  return credentials
}

export const useCredsFromCredentials = (provider: string): CredentialsPair => {
  const { data } = useResolvedUser()
  const credential = data?.user?.credentials?.find((cred) => cred.provider === provider)

  return {
    username: credential?.username ?? null,
    password: credential?.password ?? null,
  }
}

export const useHandleProtectionAuthValid = () => {
  const protectionV2Flag = useFlag('feature.protection_v2', true)
  const protectionIpipelineFlag = useFlag('feature.protection_ipipeline', false)

  // protection v2 must be turned ON, and protection ipipeline must be turned OFF
  // for the new endpoint to be in use
  const shouldUseNewEndpoint = protectionV2Flag && !protectionIpipelineFlag

  const [isProtectionAuthValid, { loading: checkNewCredsLoading, error: authValidError }] =
    useIsProtectionAuthValidLazyQuery({ fetchPolicy: 'cache-and-network' })

  const [isProtectionAuthValidV2, { loading: checkNewCredsLoadingV2, error: authValidErrorV2 }] =
    useIsProtectionAuthValidV2LazyQuery({ fetchPolicy: 'cache-and-network' })

  const authValid = shouldUseNewEndpoint ? isProtectionAuthValidV2 : isProtectionAuthValid
  const newCredsLoading = shouldUseNewEndpoint ? checkNewCredsLoadingV2 : checkNewCredsLoading
  const error = shouldUseNewEndpoint ? authValidErrorV2 : authValidError

  return {
    isProtectionAuthValid: authValid,
    checkNewCredsLoading: newCredsLoading,
    error: error,
  }
}
